import ActionConfirmationDialog from '../../../components/ActionConfirmationDialog';
import { ActionBar, Button, Dialog, Portal, Text } from '@chakra-ui/react';
import { buildSlotCreateTemplate } from './slotCreateTemplate';
import { FiCalendar, FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import { formatSlotSummary } from '@opanuj/shared/utils';
import { slotDelete } from '../../../operations/admin/slotDelete';
import { SlotForm } from './SlotForm';
import { SlotReservationDialog } from './SlotReservationForm';
import { useDatabase } from '../../../hooks/useDatabase';
import { useMemo, useState } from 'react';

function SlotSelectionActionBar({
	slot,
	availableSlots = [],
	selectedSlots = [],
	selectedSlotIds = [],
	onClearSelection,
	onDeleteSelected,
}: {
	slot: any | null;
	availableSlots?: any[];
	selectedSlots?: any[];
	selectedSlotIds?: string[];
	onClearSelection: () => void;
	onDeleteSelected?: (slotIds: string[]) => Promise<void> | void;
}) {
	const database = useDatabase();
	const [isEditOpen, setEditOpen] = useState(false);
	const [isAddSimilarOpen, setAddSimilarOpen] = useState(false);
	const [isRegisterOpen, setRegisterOpen] = useState(false);
	const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const addSimilarValues = useMemo(() => buildSlotCreateTemplate(slot), [slot]);
	const slotSummary = useMemo(() => {
		if (!slot) return '';
		return formatSlotSummary(slot);
	}, [slot]);
	const isMultiSelectionMode = selectedSlotIds.length > 1;
	const actionBarOpen = Boolean(slot) || selectedSlotIds.length > 0;

	const handleDelete = async () => {
		if (isMultiSelectionMode) {
			await onDeleteSelected?.(selectedSlotIds);
			setDeleteDialogOpen(false);
			onClearSelection();
			return;
		}
		if (!slot) return;
		await slotDelete(database, slot);
		setDeleteDialogOpen(false);
		onClearSelection();
	};

	if (!actionBarOpen) return null;

	return (
		<>
			<ActionBar.Root open={actionBarOpen}>
				<ActionBar.Positioner>
					<ActionBar.Content>
						<ActionBar.SelectionTrigger>
							<Text fontSize="sm" fontWeight="semibold">
								{isMultiSelectionMode ? `${selectedSlotIds.length} zaznaczonych` : (slotSummary || 'Slot')}
							</Text>
						</ActionBar.SelectionTrigger>
						<ActionBar.Separator />
						{isMultiSelectionMode ? (
							<>
								<Button size="sm" colorPalette="orange" variant="surface" onClick={() => setRegisterOpen(true)}>
									<FiCalendar />
									Rezerwuj
								</Button>
								<Button size="sm" colorPalette="red" variant="outline" onClick={() => setDeleteDialogOpen(true)}>
									Usuń
								</Button>
								<ActionBar.CloseTrigger asChild>
									<Button size="sm" variant="ghost" onClick={onClearSelection}>
										Anuluj
									</Button>
								</ActionBar.CloseTrigger>
							</>
						) : (
							<>
								<Button size="sm" colorPalette="red" variant="outline" onClick={() => setDeleteDialogOpen(true)}>
									<FiTrash2 />
									Usuń
								</Button>
								<Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
									<FiEdit2 />
									Edytuj
								</Button>
								<Button size="sm" colorPalette="green" variant="outline" onClick={() => setAddSimilarOpen(true)}>
									<FiPlus />
									Dodaj podobny
								</Button>
								<Button size="sm" colorPalette="orange" variant="surface" onClick={() => setRegisterOpen(true)}>
									<FiCalendar />
									Rezerwuj
								</Button>
								<ActionBar.CloseTrigger asChild>
									<Button size="sm" variant="ghost" onClick={onClearSelection}>
										Odznacz
									</Button>
								</ActionBar.CloseTrigger>
							</>
						)}
					</ActionBar.Content>
				</ActionBar.Positioner>
			</ActionBar.Root>

			<Dialog.Root
				open={isEditOpen}
				onOpenChange={(event) => {
					if (!event.open) setEditOpen(false);
				}}
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content maxW="900px" w="95vw">
							<Dialog.Header>
								<Dialog.Title>Edytuj termin</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<SlotForm slot={slot} onCancel={() => setEditOpen(false)} />
							</Dialog.Body>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>

			<SlotReservationDialog
				open={isRegisterOpen}
				availableSlots={availableSlots}
				slots={selectedSlots.length > 0 ? selectedSlots : slot ? [slot] : []}
				onClose={() => setRegisterOpen(false)}
			/>

			<Dialog.Root
				open={isAddSimilarOpen}
				onOpenChange={(event) => {
					if (!event.open) setAddSimilarOpen(false);
				}}
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content maxW="900px" w="95vw">
							<Dialog.Header>
								<Dialog.Title>Dodaj podobny termin</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<SlotForm
									initialValues={addSimilarValues ?? undefined}
									onCancel={() => setAddSimilarOpen(false)}
								/>
							</Dialog.Body>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>

			<ActionConfirmationDialog
				isOpen={isDeleteDialogOpen}
				isPending={false}
				onClose={() => setDeleteDialogOpen(false)}
				onAction={handleDelete}
				title={isMultiSelectionMode ? 'Usuń zaznaczone terminy' : 'Usuń termin'}
				bodyText={isMultiSelectionMode
					? `Czy na pewno chcesz usunąć ${selectedSlotIds.length} zaznaczonych terminów? Ta operacja usunie również powiązane rezerwacje.`
					: 'Czy na pewno chcesz usunąć ten termin? Ta operacja usunie również powiązane rezerwacje.'}
				actionText="Usuń"
				actionColor="red"
			/>
		</>
	);
}

export default SlotSelectionActionBar;
